// Actor display-name resolution for audit rows and id-stamped evidence.
//
// The API enriches audit rows with `actorName` (the users.name joined on
// keycloak_id — see GET /cases/:id/audit and /audit). When that's present we
// show it. Otherwise we fall back to the raw id so the value is never blank.
//
// R6 / L-5 removed the LEGACY_ACTOR_NAMES U1–U4 shim: those legacy short ids
// were nulled out by the Phase-11 seed loader (scripts/seed-initial-cases.ts
// maps non-UUID analyst/approver ids to NULL), so they cannot appear in live
// data, and the dead `AuditEntry.actor` field they keyed off doesn't match the
// API wire shape.
export interface ActorRef {
  actorId?: string | null;
  actorName?: string | null;
}

export function resolveActorName(entry: ActorRef): string {
  if (entry.actorName) return entry.actorName;
  if (entry.actorId) return entry.actorId;
  return 'unknown';
}

// #776 — a PERSON's name on a case screen, which must never be a raw id.
//
// `resolveActorName` falls back to the id because on an audit row an id is
// better than a blank. On the Decision, Overview and KYC tabs it is not: those
// read "Recommended approve by d1cd4f84-…" to an approver, which is the bug
// #776 was filed for. So a UUID-shaped id with no name renders as
// `unknownLabel`, never as itself.
//
// A NON-UUID id is shown as it is. Seed and dev-realm evidence carries subs
// like 'system' and 'kc-analyst', which name something a reader recognises and
// which no `users` row will ever resolve — hiding them would turn a legible
// "system" into "Unknown user".
//
// `self` resolves the reader's own sub before the server has projected a name:
// evidence this client just filed carries `verifiedBy: user.id` until the
// PATCH response brings the name map back.
const UUID_SHAPED = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function personName(
  id: string | null | undefined,
  name: string | null | undefined,
  opts: { unknownLabel?: string; self?: { id: string; name: string } } = {},
): string {
  const unknownLabel = opts.unknownLabel ?? 'Unknown user';
  if (name) return name;
  if (!id) return unknownLabel;
  if (opts.self && id === opts.self.id) return opts.self.name;
  return UUID_SHAPED.test(id) ? unknownLabel : id;
}
